/**
 * digitMask.js — CloudGM Customer
 * Client-side helper to render masked phone/plate numbers when the backend
 * has already applied masking (item.displayNumber / item.displayPlateNumber),
 * and as a defensive fallback if the raw number + hiddenDigits are present.
 *
 * IMPORTANT: The backend already strips `number` / `plateNumber` and exposes
 * `displayNumber` / `displayPlateNumber`. This util's primary job is to READ
 * the already-masked value; the local `applyMask` is a safety net.
 */

export const MASK_LETTERS = ['X', 'Y', 'Z'];

/**
 * Apply the XYZ mask (matches CloudGM Business).
 * Same underlying digit → same letter. Max 3 distinct; 4th+ collapse to Z.
 */
export function applyMask(value, hiddenDigits) {
  if (!value) return value;
  if (!Array.isArray(hiddenDigits) || hiddenDigits.length === 0) return value;
  const hidden = new Set(hiddenDigits.map((n) => parseInt(n, 10)).filter((n) => !Number.isNaN(n)));
  if (hidden.size === 0) return value;

  const digitToLetter = {};
  let stripped = 0;
  let out = '';
  for (const ch of String(value)) {
    if (/[A-Za-z0-9\u0600-\u06FF]/.test(ch)) {
      if (hidden.has(stripped)) {
        if (digitToLetter[ch]) {
          out += digitToLetter[ch];
        } else {
          const idx = Object.keys(digitToLetter).length;
          const letter = MASK_LETTERS[idx] || MASK_LETTERS[MASK_LETTERS.length - 1];
          digitToLetter[ch] = letter;
          out += letter;
        }
      } else {
        out += ch;
      }
      stripped += 1;
    } else {
      out += ch;
    }
  }
  return out;
}

/**
 * Prefer server-provided displayNumber (which never contains the real digits).
 * Fallback to local mask if backend still returns `number` + `hiddenDigits`.
 */
export function getDisplayNumber(item) {
  if (!item) return '';
  if (item.displayNumber) return item.displayNumber;
  if (item.number && Array.isArray(item.hiddenDigits) && item.hiddenDigits.length > 0) {
    return applyMask(item.number, item.hiddenDigits);
  }
  return item.number || '';
}

export function getDisplayPlateNumber(item) {
  if (!item) return '';
  if (item.displayPlateNumber) return item.displayPlateNumber;
  const raw = item.plateNumber || item.plate_number;
  if (raw && Array.isArray(item.hiddenDigits) && item.hiddenDigits.length > 0) {
    return applyMask(raw, item.hiddenDigits);
  }
  return raw || '';
}

/**
 * Returns true when the item has any digit hidden — used to disable
 * copy-to-clipboard buttons in the UI.
 */
export function isNumberMasked(item) {
  return Array.isArray(item?.hiddenDigits) && item.hiddenDigits.length > 0;
}

/**
 * Split a UAE mobile number (or its XYZ-masked display) into [operator code, rest]:
 *   "050-7776677" → ["050", "7776677"]   ·   "971507776677" → ["+971 50", "7776677"]
 */
export function splitPhoneDisplay(value) {
  const raw = String(value || '').replace(/[\s-]/g, '');
  if (!raw) return ['—', ''];
  if (raw.length === 12 && raw.startsWith('971')) return [`+971 ${raw.slice(3, 5)}`, raw.slice(5)];
  return [raw.slice(0, 3), raw.slice(3)];
}
